/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import React, {
  forwardRef,
  ReactElement,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal } from 'components/UiControls/modal/modal';
import { ModalOptions } from 'utils/enum';
import { isNaN } from 'lodash';
import { Switcher } from 'components/shared/Switcher';
import { ApiLimitsService, ApiLimits, ApiLimitMetric } from 'services/api-limits.service';
import { UsageTrackingService } from 'services/usage-tracking.service';
import { customToast } from 'utils/toast.util';
import Loader from 'components/loader/loader';
import { Notification } from 'components/UiControls/notification/Notification';

type IPromiseParams = {
  resolve: (payload: { option: ModalOptions }) => void;
  reject: (err: any) => void;
};

export type ManageLimitsModalRef = {
  show: () => Promise<{ option: ModalOptions }>;
};

type Props = {};

type LimitField = keyof ApiLimitMetric;

const METRIC_LABELS: Record<LimitField, { label: string; unit: string }> = {
  totalApiTranscribed: { label: 'Total API Transcribed', unit: 'minutes' },
  apiCalls: { label: 'API Calls', unit: 'calls' },
  totalComputeTime: { label: 'Total Compute Time', unit: 'minutes' },
  dailyApiRate: { label: 'Daily API Rate', unit: 'requests/day' },
};

const METRIC_KEYS: LimitField[] = [
  'totalApiTranscribed',
  'apiCalls',
  'totalComputeTime',
  'dailyApiRate',
];

export const ManageLimitsModal = forwardRef(
  (props: Props, ref: React.Ref<unknown>): ReactElement => {
    useImperativeHandle(ref, () => ({ show }));

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [limits, setLimits] = useState<ApiLimits>({
      hardLimits: {
        totalApiTranscribed: null,
        apiCalls: null,
        totalComputeTime: null,
        dailyApiRate: null,
      },
      softLimits: {
        totalApiTranscribed: null,
        apiCalls: null,
        totalComputeTime: null,
        dailyApiRate: null,
      },
      softLimitEnabled: false,
    });
    const [hardLimitErrors, setHardLimitErrors] = useState<
      Partial<Record<LimitField, string>>
    >({});
    const [softLimitErrors, setSoftLimitErrors] = useState<
      Partial<Record<LimitField, string>>
    >({});
    const [currentUsage, setCurrentUsage] = useState<ApiLimitMetric>({
      totalApiTranscribed: 0,
      apiCalls: 0,
      totalComputeTime: 0,
      dailyApiRate: 0,
    });
    // Track saved soft limits for toggle behavior
    const [savedSoftLimits, setSavedSoftLimits] = useState<ApiLimitMetric>({
      totalApiTranscribed: null,
      apiCalls: null,
      totalComputeTime: null,
      dailyApiRate: null,
    });
    // Track edited soft limits during current session (for toggle Case 2)
    const [editedSoftLimits, setEditedSoftLimits] = useState<ApiLimitMetric | null>(null);


    const hasValidationErrors = 
      Object.keys(hardLimitErrors).length > 0 || 
      Object.keys(softLimitErrors).length > 0;

    const handleHardLimitNotificationClose = (name?: string) => {
      if (name) {
        setHardLimitErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name as LimitField];
          return newErrors;
        });
      }
    };

    const handleSoftLimitNotificationClose = (name?: string) => {
      if (name) {
        setSoftLimitErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name as LimitField];
          return newErrors;
        });
      }
    };

    const promiseInfo = useRef<IPromiseParams>({
      resolve: () => {},
      reject: () => {},
    });

    const show = async (): Promise<{ option: ModalOptions }> => {
      return new Promise((resolve, reject) => {
        promiseInfo.current = {
          resolve,
          reject,
        };

        // Load existing limits
        loadLimits();
        setIsOpen(true);
      });
    };

    const loadLimits = async () => {
      try {
        setIsLoading(true);
        
        // Fetch limits (required)
        const limitsResponse = await ApiLimitsService.getApiLimits();
        const limitsData = limitsResponse.data.data;
        
        // Fetch current usage (optional - don't fail if unavailable)
        let currentUsageData: ApiLimitMetric = {
          totalApiTranscribed: 0,
          apiCalls: 0,
          totalComputeTime: 0,
          dailyApiRate: 0,
        };
        
        try {
          const usageResponse = await UsageTrackingService.getUsageSummary();
          const usageData = usageResponse.data.data;
          
          // Convert usage data to match API limit metrics
          // Note: Despite the field name, totalHoursTranscribed is already in minutes
          currentUsageData = {
            totalApiTranscribed: usageData.totalHoursTranscribed, // Already in minutes
            apiCalls: usageData.apiCalls,
            totalComputeTime: usageData.totalComputeTimeMinutes,
            dailyApiRate: 0, // Daily API rate is not available in current usage
          };
        } catch (usageError) {
          console.warn('Could not fetch current usage data. Validation against current usage will use default values (0).', usageError);
          // Continue with default usage values (0) - modal will still open
        }
        
        // Case 4: If Soft Limit is enabled but all values are null, disable it
        const hasAnySoftLimitValue = Object.values(limitsData.softLimits).some(val => val !== null);
        const normalizedLimits = {
          ...limitsData,
          softLimitEnabled: limitsData.softLimitEnabled && hasAnySoftLimitValue,
        };
        
        setLimits(normalizedLimits);
        setSavedSoftLimits(normalizedLimits.softLimits);
        setCurrentUsage(currentUsageData);
        setEditedSoftLimits(null);
        setHardLimitErrors({});
        setSoftLimitErrors({});
      } catch (error) {
        console.error('Error loading API limits:', error);
        customToast.error('Failed to load limit settings');
      } finally {
        setIsLoading(false);
      }
    };

    const hideModal = () => {
      setIsOpen(false);
      setHardLimitErrors({});
      setSoftLimitErrors({});
      // Reset edited soft limits on cancel (to ensure Case 3 works correctly)
      setEditedSoftLimits(null);
    };

    const handleCancel = () => {
      hideModal();
      promiseInfo.current?.resolve({ option: ModalOptions.CANCEL });
    };

    const validateLimits = (): boolean => {
      const hardErrors: Partial<Record<LimitField, string>> = {};
      const softErrors: Partial<Record<LimitField, string>> = {};

      // Define metrics that need current usage validation
      const usageMetrics: LimitField[] = ['totalApiTranscribed', 'apiCalls', 'totalComputeTime'];

      METRIC_KEYS.forEach((key) => {
        const hardValue = limits.hardLimits[key];
        const softValue = limits.softLimits[key];
        const usage = currentUsage[key] ?? 0;

        // Validate Hard Limit
        if (hardValue !== null) {
          if (key === 'dailyApiRate') {
            // Daily API Rate Hard Limit validation
            const apiCallsHard = limits.hardLimits.apiCalls;
            const softLimit = limits.softLimits.dailyApiRate;
            
            // Check: Hard > Soft (if soft enabled and set)
            if (limits.softLimitEnabled && softLimit !== null && hardValue <= softLimit) {
              hardErrors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
            }
            // Check: Daily API Rate Hard ≤ API Calls Hard (if API Calls Hard is set)
            else if (apiCallsHard !== null && hardValue > apiCallsHard) {
              hardErrors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
            }
          } else if (usageMetrics.includes(key)) {
            // Usage-based metrics Hard Limit validation
            if (hardValue <= usage) {
              hardErrors[key] = 'Hard Limit must be greater than current usage.';
            }
          }
        }

        // Validate Soft Limit when enabled
        if (limits.softLimitEnabled && softValue !== null) {
          if (key === 'dailyApiRate') {
            // Daily API Rate Soft Limit validation
            const apiCallsSoft = limits.softLimits.apiCalls;
            
            // Check: Soft < Hard (if hard is set)
            if (hardValue !== null && softValue >= hardValue) {
              softErrors[key] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
            }
            // Check: Daily API Rate Soft ≤ API Calls Soft (if API Calls Soft is set)
            else if (apiCallsSoft !== null && softValue > apiCallsSoft) {
              softErrors[key] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
            }
          } else if (usageMetrics.includes(key)) {
            // Usage-based metrics Soft Limit validation
            // Check: Soft > usage
            if (softValue <= usage) {
              softErrors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
            }
            // Check: Soft < Hard (if hard is set)
            else if (hardValue !== null && softValue >= hardValue) {
              softErrors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
            }
          }
        }
      });

      setHardLimitErrors(hardErrors);
      setSoftLimitErrors(softErrors);
      return Object.keys(hardErrors).length === 0 && Object.keys(softErrors).length === 0;
    };

    const handleSave = async () => {
      if (!validateLimits()) {
        return;
      }

      try {
        setIsLoading(true);
        
        // Case 4: If Soft Limit is enabled but all values are null, disable it
        const hasAnySoftLimitValue = Object.values(limits.softLimits).some(val => val !== null);
        const limitsToSave = {
          ...limits,
          softLimitEnabled: limits.softLimitEnabled && hasAnySoftLimitValue,
        };
        
        const response = await ApiLimitsService.saveApiLimits(limitsToSave);
        const message = response.data.message || 'Limit settings have been saved successfully.';
        customToast.success(message);
        
        // Update saved soft limits after successful save
        setSavedSoftLimits(limitsToSave.softLimits);
        setEditedSoftLimits(null);
        
        hideModal();
        promiseInfo.current?.resolve({ option: ModalOptions.YES });
      } catch (error) {
        console.error('Error saving API limits:', error);
        customToast.error('Failed to save limit settings');
      } finally {
        setIsLoading(false);
      }
    };

    const handleHardLimitChange = (key: LimitField, value: number | null) => {
      const numValue = value !== null && value > 0 ? value : null;
      const newHardLimits = {
        ...limits.hardLimits,
        [key]: numValue,
      };
      
      setLimits((prev) => ({
        ...prev,
        hardLimits: newHardLimits,
      }));
      
      // Perform inline validation for Hard Limit
      const errors: Partial<Record<LimitField, string>> = {};
      const usage = currentUsage[key] ?? 0;
      const usageMetrics: LimitField[] = ['totalApiTranscribed', 'apiCalls', 'totalComputeTime'];
      
      if (numValue !== null) {
        if (key === 'dailyApiRate') {
          // Daily API Rate Hard Limit validation
          const apiCallsHard = newHardLimits.apiCalls;
          const softLimit = limits.softLimits.dailyApiRate;
          
          // Check: Hard > Soft (if soft enabled and set)
          if (limits.softLimitEnabled && softLimit !== null && numValue <= softLimit) {
            errors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
          // Check: Daily API Rate Hard ≤ API Calls Hard
          else if (apiCallsHard !== null && numValue > apiCallsHard) {
            errors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
        } else if (key === 'apiCalls') {
          // API Calls Hard Limit validation
          // Check: Hard > usage
          if (numValue <= usage) {
            errors[key] = 'Hard Limit must be greater than current usage.';
          }
          
          // Cross-field: Check if Daily API Rate Hard needs revalidation
          const dailyApiRateHard = newHardLimits.dailyApiRate;
          if (dailyApiRateHard !== null && dailyApiRateHard > numValue) {
            errors['dailyApiRate'] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
        } else if (usageMetrics.includes(key)) {
          // Usage-based metrics Hard Limit validation
          // Check: Hard > usage
          if (numValue <= usage) {
            errors[key] = 'Hard Limit must be greater than current usage.';
          }
        }
      }
      
      // Update Hard Limit validation errors
      setHardLimitErrors((prev) => {
        const newErrors = { ...prev };
        
        // Update or remove error for current key
        if (errors[key]) {
          newErrors[key] = errors[key]!;
        } else {
          delete newErrors[key];
        }
        
        // Handle cross-field validation for dailyApiRate when changing apiCalls
        if (key === 'apiCalls') {
          if (errors['dailyApiRate']) {
            newErrors['dailyApiRate'] = errors['dailyApiRate']!;
          } else if (prev['dailyApiRate']) {
            // Revalidate dailyApiRate to see if error should be cleared
            const dailyApiRateHard = newHardLimits.dailyApiRate;
            if (dailyApiRateHard !== null) {
              const softLimit = limits.softLimits.dailyApiRate;
              const hasError = 
                (limits.softLimitEnabled && softLimit !== null && dailyApiRateHard <= softLimit) ||
                (numValue !== null && dailyApiRateHard > numValue);
              
              if (!hasError) {
                delete newErrors['dailyApiRate'];
              }
            }
          }
        }
        
        return newErrors;
      });
      
      // Re-validate Soft Limits when Hard Limit changes (cross-validation)
      if (limits.softLimitEnabled) {
        setSoftLimitErrors((prev) => {
          const newSoftErrors = { ...prev };
          const softValue = limits.softLimits[key];
          
          if (softValue !== null && numValue !== null) {
            if (key === 'dailyApiRate') {
              // Daily API Rate Soft Limit cross-validation
              // Check: Soft < Hard
              if (softValue >= numValue) {
                newSoftErrors[key] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
              } else {
                // Clear error only if it exists
                if (prev[key]) {
                  delete newSoftErrors[key];
                }
              }
            } else if (usageMetrics.includes(key)) {
              // Usage-based metrics Soft Limit cross-validation
              const usage = currentUsage[key] ?? 0;
              
              if (softValue <= usage || softValue >= numValue) {
                newSoftErrors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
              } else {
                // Clear error only if it exists
                if (prev[key]) {
                  delete newSoftErrors[key];
                }
              }
            }
          } else if (softValue !== null && numValue === null) {
            // Hard limit was cleared, but soft limit still exists
            // Only clear soft limit error if it was related to hard limit comparison
            if (prev[key] && (key === 'dailyApiRate' || usageMetrics.includes(key))) {
              delete newSoftErrors[key];
            }
          }
          
          return newSoftErrors;
        });
      }
    };

    const handleSoftLimitChange = (key: LimitField, value: number | null) => {
      const numValue = value !== null && value > 0 ? value : null;
      const newSoftLimits = {
        ...limits.softLimits,
        [key]: numValue,
      };
      
      setLimits((prev) => ({
        ...prev,
        softLimits: newSoftLimits,
      }));
      
      // Track edited soft limits (for toggle Case 2)
      setEditedSoftLimits(newSoftLimits);
      
      // Perform inline validation for Soft Limit
      const errors: Partial<Record<LimitField, string>> = {};
      const hardValue = limits.hardLimits[key];
      const usage = currentUsage[key] ?? 0;
      const usageMetrics: LimitField[] = ['totalApiTranscribed', 'apiCalls', 'totalComputeTime'];
      
      if (numValue !== null) {
        if (key === 'dailyApiRate') {
          // Daily API Rate Soft Limit validation
          const apiCallsSoft = newSoftLimits.apiCalls;
          
          // Check: Soft < Hard (if hard is set)
          if (hardValue !== null && numValue >= hardValue) {
            errors[key] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
          // Check: Daily API Rate Soft ≤ API Calls Soft
          else if (apiCallsSoft !== null && numValue > apiCallsSoft) {
            errors[key] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
        } else if (key === 'apiCalls') {
          // API Calls Soft Limit validation
          // Check: Soft > usage
          if (numValue <= usage) {
            errors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
          }
          // Check: Soft < Hard (if hard is set)
          else if (hardValue !== null && numValue >= hardValue) {
            errors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
          }
          
          // Cross-field: Check if Daily API Rate Soft needs revalidation
          const dailyApiRateSoft = newSoftLimits.dailyApiRate;
          if (dailyApiRateSoft !== null && dailyApiRateSoft > numValue) {
            errors['dailyApiRate'] = 'Soft Limit must be less than the Hard Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
          }
        } else if (usageMetrics.includes(key)) {
          // Usage-based metrics Soft Limit validation
          // Check: Soft > usage
          if (numValue <= usage) {
            errors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
          }
          // Check: Soft < Hard (if hard is set)
          else if (hardValue !== null && numValue >= hardValue) {
            errors[key] = 'Soft Limit must be greater than current usage and less than the Hard Limit.';
          }
        }
      }
      
      // Update Soft Limit validation errors
      setSoftLimitErrors((prev) => {
        const newErrors = { ...prev };
        
        // Update or remove error for current key
        if (errors[key]) {
          newErrors[key] = errors[key]!;
        } else {
          delete newErrors[key];
        }
        
        // Handle cross-field validation for dailyApiRate when changing apiCalls
        if (key === 'apiCalls') {
          if (errors['dailyApiRate']) {
            newErrors['dailyApiRate'] = errors['dailyApiRate']!;
          } else if (prev['dailyApiRate']) {
            // Revalidate dailyApiRate to see if error should be cleared
            const dailyApiRateSoft = newSoftLimits.dailyApiRate;
            if (dailyApiRateSoft !== null) {
              const hardLimit = limits.hardLimits.dailyApiRate;
              const hasError = 
                (hardLimit !== null && dailyApiRateSoft >= hardLimit) ||
                (numValue !== null && dailyApiRateSoft > numValue);
              
              if (!hasError) {
                delete newErrors['dailyApiRate'];
              }
            }
          }
        }
        
        return newErrors;
      });
      
      // Re-validate Hard Limits when Soft Limit changes (cross-validation)
      setHardLimitErrors((prev) => {
        const newHardErrors = { ...prev };
        const hardValue = limits.hardLimits[key];
        
        if (hardValue !== null && numValue !== null) {
          if (key === 'dailyApiRate') {
            // Daily API Rate Hard Limit cross-validation
            // Check: Hard > Soft
            if (hardValue <= numValue) {
              newHardErrors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
            } else {
              // Clear error only if it exists and was related to soft limit
              if (prev[key] && prev[key]!.includes('Soft Limit')) {
                // Re-check if there are other errors (API Calls constraint)
                const apiCallsHard = limits.hardLimits.apiCalls;
                if (apiCallsHard !== null && hardValue > apiCallsHard) {
                  newHardErrors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
                } else {
                  delete newHardErrors[key];
                }
              }
            }
          } else if (usageMetrics.includes(key)) {
            // Usage-based metrics Hard Limit cross-validation
            const usage = currentUsage[key] ?? 0;
            
            // Hard limit only needs to be > usage (not > soft)
            // So we only clear the error if soft limit is now valid
            if (prev[key]) {
              // Re-check hard limit validation
              if (hardValue <= usage) {
                newHardErrors[key] = 'Hard Limit must be greater than current usage.';
              } else {
                delete newHardErrors[key];
              }
            }
          }
        } else if (hardValue !== null && numValue === null) {
          // Soft limit was cleared, check if we should clear hard limit error
          if (prev[key] && prev[key]!.includes('Soft Limit')) {
            if (key === 'dailyApiRate') {
              // Re-check if there are other errors (API Calls constraint)
              const apiCallsHard = limits.hardLimits.apiCalls;
              if (apiCallsHard !== null && hardValue > apiCallsHard) {
                newHardErrors[key] = 'Hard Limit must be greater than the Soft Limit. If API Calls is set, Daily API Rate ≤ API Calls.';
              } else {
                delete newHardErrors[key];
              }
            } else {
              // For usage-based metrics, re-validate against usage only
              const usage = currentUsage[key] ?? 0;
              if (hardValue <= usage) {
                newHardErrors[key] = 'Hard Limit must be greater than current usage.';
              } else {
                delete newHardErrors[key];
              }
            }
          }
        }
        
        return newHardErrors;
      });
    };

    const handleSoftLimitToggle = (checked: boolean) => {
      if (checked) {
        // Turning ON the toggle
        let limitsToRestore: ApiLimitMetric;
        
        // Case 2: If we have edited values in current session, use those
        if (editedSoftLimits !== null) {
          limitsToRestore = editedSoftLimits;
        }
        // Case 1 & Case 3: If we have previously saved values, use those
        else if (Object.values(savedSoftLimits).some(val => val !== null)) {
          limitsToRestore = savedSoftLimits;
        }
        // Case 4: No saved values, keep current (likely all null)
        else {
          limitsToRestore = limits.softLimits;
        }
        
        setLimits((prev) => ({
          ...prev,
          softLimitEnabled: true,
          softLimits: limitsToRestore,
        }));
      } else {
        // Turning OFF the toggle
        // Save current edited state before disabling
        if (limits.softLimitEnabled) {
          setEditedSoftLimits(limits.softLimits);
        }
        
        setLimits((prev) => ({
          ...prev,
          softLimitEnabled: false,
        }));
        
        // Clear soft limit validation errors when disabling soft limits
        setSoftLimitErrors({});
      }
    };

    // Helper function to parse and validate input value
    const parseInputValue = (value: string): number | null => {
      if (value === '') {
        return null;
      }
      const numValue = parseInt(value, 10);
      return !isNaN(numValue) && numValue >= 0 ? numValue : null;
    };

    // Handler for hard limit input change
    const handleHardLimitInputChange = (key: LimitField, value: string) => {
      if (value === '') {
        handleHardLimitChange(key, null);
      } else {
        const numValue = parseInputValue(value);
        if (numValue !== null) {
          handleHardLimitChange(key, numValue);
        }
      }
    };

    // Handler for hard limit input blur
    const handleHardLimitInputBlur = (key: LimitField, value: string) => {
      const numValue = parseInputValue(value);
      if (numValue === null) {
        handleHardLimitChange(key, null);
      }
    };

    // Handler for soft limit input change
    const handleSoftLimitInputChange = (key: LimitField, value: string) => {
      if (value === '') {
        handleSoftLimitChange(key, null);
      } else {
        const numValue = parseInputValue(value);
        if (numValue !== null) {
          handleSoftLimitChange(key, numValue);
        }
      }
    };

    // Handler for soft limit input blur
    const handleSoftLimitInputBlur = (key: LimitField, value: string) => {
      const numValue = parseInputValue(value);
      if (numValue === null) {
        handleSoftLimitChange(key, null);
      }
    };

    return (
      <div css={[customModalCss]}>
        <Modal
          show={isOpen}
          modalClosed={() => {
            hideModal();
            promiseInfo.current?.resolve({ option: ModalOptions.CLOSE });
          }}
        >
          <div tw="p-6 md-down:p-4">
            <h2 tw="font-semibold text-sonnant-dark text-4xl md-down:text-3xl mb-6 md-down:mb-4">
              Manage Limits for Tenant
            </h2>

            {isLoading ? (
              <div tw="py-8 flex justify-center items-center">
                <Loader />
              </div>
            ) : (
              <div tw="space-y-6">
                {/* Hard Limit Section */}
                <div>
                  <h3 tw="font-semibold text-2xl md-down:text-xl mb-4 text-sonnant-dark">
                    Hard Limit
                  </h3>
                  <div tw="space-y-4">
                    {METRIC_KEYS.map((key) => {
                      const { label, unit } = METRIC_LABELS[key];
                      const hasError = !!hardLimitErrors[key];
                      return (
                          <div key={key} tw="flex items-start md-down:flex-col md-down:items-start md-down:space-y-2">
                            <label tw="w-[16rem] md-down:w-full text-15 md-down:text-14 font-medium pt-[1.1rem] md-down:pt-0 mr-4 md-down:mr-0">{label}</label>
                            <div tw="flex flex-col md-down:w-full">
                              <div tw="flex items-center space-x-3 md-down:space-x-2">
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={limits.hardLimits[key] ?? ''}
                                  onChange={(e) => handleHardLimitInputChange(key, e.target.value)}
                                  onBlur={(e) => handleHardLimitInputBlur(key, e.target.value)}
                                  tw="w-[14rem] md-down:w-full md-down:flex-1 h-[3.5rem] md-down:h-[3rem] border-width[2px] border-sonnant-grey-3 rounded px-4 md-down:px-3 text-15 md-down:text-14 focus:(border-sonnant-purple-2 outline-none)"
                                  className="keywordInput"
                                  placeholder=""
                                />
                                <span tw="w-[8rem] md-down:w-auto md-down:min-w-[6rem] text-14 md-down:text-13 text-sonnant-grey-6 font-medium">
                                  {unit}
                                </span>
                              </div>
                              <Notification
                                name={key}
                                close={handleHardLimitNotificationClose}
                                type={hasError ? 'error' : null}
                                message={hardLimitErrors[key] || ''}
                              />
                            </div>
                          </div>
                      );
                    })}
                  </div>
                </div>

                {/* Soft Limit Toggle */}
                <div tw="flex items-center space-x-4 md-down:flex-col md-down:items-start md-down:space-x-0 md-down:space-y-3 pt-6 border-t border-sonnant-grey-light">
                  <Switcher
                    checked={limits.softLimitEnabled}
                    onCheckedChange={handleSoftLimitToggle}
                    hasLabel
                    onLabel="ON"
                    offLabel="OFF"
                    color="#00AF7A"
                  />
                  <label tw="text-15 md-down:text-14 font-medium">
                    Enable Soft Limit Triggers proactive alerts
                  </label>
                </div>

                {/* Soft Limit Section */}
                {limits.softLimitEnabled && (
                  <div>
                    <h3 tw="font-semibold text-2xl md-down:text-xl mb-4 text-sonnant-dark">
                      Soft Limit
                    </h3>
                    <div tw="space-y-4">
                      {METRIC_KEYS.map((key) => {
                        const { label, unit } = METRIC_LABELS[key];
                        const hasError = !!softLimitErrors[key];
                        return (
                          <div key={key} tw="flex items-start md-down:flex-col md-down:items-start md-down:space-y-2">
                            <label tw="w-[16rem] md-down:w-full text-15 md-down:text-14 font-medium pt-[1.1rem] md-down:pt-0 mr-4 md-down:mr-0">{label}</label>
                            <div tw="flex flex-col md-down:w-full">
                              <div tw="flex items-center space-x-3 md-down:space-x-2">
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={limits.softLimits[key] ?? ''}
                                  onChange={(e) => handleSoftLimitInputChange(key, e.target.value)}
                                  onBlur={(e) => handleSoftLimitInputBlur(key, e.target.value)}
                                  tw="w-[14rem] md-down:w-full md-down:flex-1 h-[3.5rem] md-down:h-[3rem] border-width[2px] border-sonnant-grey-3 rounded px-4 md-down:px-3 text-15 md-down:text-14 focus:(border-sonnant-purple-2 outline-none)"
                                  className="keywordInput"
                                  placeholder=""
                                />
                                <span tw="w-[8rem] md-down:w-auto md-down:min-w-[6rem] text-14 md-down:text-13 text-sonnant-grey-6 font-medium">
                                  {unit}
                                </span>
                              </div>
                              <Notification
                                name={key}
                                close={handleSoftLimitNotificationClose}
                                type={hasError ? 'error' : null}
                                message={softLimitErrors[key] || ''}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div tw="mt-8 md-down:mt-6 pt-6 md-down:pt-4 border-t border-sonnant-grey-light">
                  <div tw="w-full flex justify-end md-down:flex-col md-down:space-y-3 md-down:space-x-0 space-x-3">
                    <button
                      type="button"
                      className="button btn-secondary large"
                      tw="flex items-center justify-center min-w-[10rem] md-down:w-full md-down:min-w-[unset] hover:(border-sonnant-grey-3!)"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                     <button
                       type="button"
                       className="button btn-primary large"
                       tw="flex items-center justify-center min-w-[10rem] md-down:w-full md-down:min-w-[unset]"
                       onClick={handleSave}
                       disabled={isLoading || hasValidationErrors}
                     >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    );
  },
);

const customModalCss = css`
  .Modal {
    ${tw`(width[65rem] md-down:width[95vw] md-down:max-width[95vw] left[calc(50% - 32.5rem)] md-down:left[2.5vw] max-height[90vh] overflow-y-auto)!`}
    background: #f9f9f9;
    border-radius: 8px;
    padding: 0;
  }
  
  .customClose {
    ${tw`md-down:(right[1.5rem] top[1.5rem])`}
    right: 2rem;
    top: 2rem;
  }
`;

